import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

// Map of route paths to their lazy import functions
const routeImports: Record<string, () => Promise<unknown>> = {
  "/fulfillment": () => import("@/pages/Fulfillment"),
  "/pre-fba": () => import("@/pages/PreFba"),
  "/seller-fulfilled-prime": () => import("@/pages/SellerFulfilledPrime"),
  "/lohnverpackung": () => import("@/pages/Lohnverpackung"),
  "/aktionsversand": () => import("@/pages/Aktionsversand"),
  "/retouren": () => import("@/pages/Retouren"),
  "/dashboard": () => import("@/pages/Dashboard"),
  "/preise": () => import("@/pages/Preise"),
  
  "/faq": () => import("@/pages/FAQ"),
  "/ueber-uns": () => import("@/pages/UeberUns"),
  "/greenmoose": () => import("@/pages/Greenmoose"),
  "/qualitaetsversprechen": () => import("@/pages/Qualitaetsversprechen"),
  "/fairplay": () => import("@/pages/Fairplay"),
  "/karriere": () => import("@/pages/Karriere"),
  "/tools": () => import("@/pages/Tools"),
  "/blog": () => import("@/pages/Blog"),
  "/kontakt": () => import("@/pages/Kontakt"),
  "/anfrage": () => import("@/pages/Anfrage"),
  "/integrationen": () => import("@/pages/Integrationen"),
};

const prefetched = new Set<string>();

// Prefetch query data for specific routes
const queryPrefetchers: Record<string, (queryClient: ReturnType<typeof useQueryClient>) => void> = {
  "/blog": (queryClient) => {
    queryClient.prefetchQuery({
      queryKey: ["blog-posts"],
      queryFn: async () => {
        const { data, error } = await supabase
          .from("blog_posts")
          .select("id, title, slug, excerpt, published, created_at, featured_image")
          .eq("published", true)
          .order("created_at", { ascending: false });
        if (error) throw error;
        return data || [];
      },
      staleTime: 1000 * 60 * 30,
    });
  },
  "/faq": (queryClient) => {
    queryClient.prefetchQuery({
      queryKey: ["faqs"],
      queryFn: async () => {
        const { remoteSupabase } = await import("@/integrations/supabase/remote-client");
        const { data, error } = await remoteSupabase
          .from("faq_items")
          .select("*, faq_categories(name)")
          .eq("is_published", true)
          .order("sort_order", { ascending: true });
        if (error) throw error;
        return (data || []).map((item: any) => ({
          id: item.id,
          question: item.question,
          answer: item.answer,
          category: item.faq_categories?.name || item.category || "Allgemein",
          sort_order: item.sort_order ?? 0,
        }));
      },
      staleTime: 1000 * 60 * 30,
    });
  },
};

export function usePrefetch() {
  const queryClient = useQueryClient();

  const prefetch = useCallback((path: string) => {
    if (prefetched.has(path)) return;
    prefetched.add(path);

    // Prefetch route chunk
    const loader = routeImports[path];
    if (loader) loader();

    // Prefetch query data in parallel
    const queryPrefetcher = queryPrefetchers[path];
    if (queryPrefetcher) queryPrefetcher(queryClient);
  }, [queryClient]);

  return prefetch;
}
